import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { SearchIcon, FilterIcon, FileUpIcon, CheckCircleIcon, AlertCircleIcon, HelpCircleIcon, FileTextIcon, FileDownIcon } from 'lucide-react';
import EmptyState from '../components/common/EmptyState';
import StatusBadge from '../components/common/StatusBadge';
import DateRangePicker from '../components/common/DateRangePicker';
import MultiSelect from '../components/common/MultiSelect';
import ExportModal from '../components/modals/ExportModal';
const Reconciliation = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    tourCode: '',
    variance: '',
    dateRange: {
      start: '',
      end: ''
    },
    airlineCodes: []
  });
  // Mock reconciliation data
  const [reconciliationData, setReconciliationData] = useState([{
    id: 1,
    groupId: 'TG-2023-001',
    tourCode: 'HKGTOUR23',
    ticketCount: 2,
    matchedCount: 1,
    mismatchCount: 0,
    unmatchedCount: 1,
    systemTotal: 3000.0,
    billingTotal: 1500.0,
    variance: 1500.0,
    status: 'partial',
    lastUpdated: '2023-06-18'
  }, {
    id: 2,
    groupId: 'TG-2023-002',
    tourCode: 'JPTOUR23',
    ticketCount: 3,
    matchedCount: 3,
    mismatchCount: 0,
    unmatchedCount: 0,
    systemTotal: 4500.0,
    billingTotal: 4500.0,
    variance: 0.0,
    status: 'complete',
    lastUpdated: '2023-06-20'
  }, {
    id: 3,
    groupId: 'TG-2023-003',
    tourCode: 'USATOUR23',
    ticketCount: 5,
    matchedCount: 0,
    mismatchCount: 0,
    unmatchedCount: 5,
    systemTotal: 7500.0,
    billingTotal: 0.0,
    variance: 7500.0,
    status: 'pending',
    lastUpdated: '2023-06-22'
  }]);
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };
  const clearFilters = () => {
    setFilters({
      status: '',
      tourCode: '',
      variance: '',
      dateRange: {
        start: '',
        end: ''
      },
      airlineCodes: []
    });
  };
  // Filter reconciliation data based on search term
  const filteredData = reconciliationData.filter(item => item.groupId.includes(searchTerm) || item.tourCode.toLowerCase().includes(searchTerm.toLowerCase()));
  return <div className="space-y-6" data-id="element-1543">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-1544">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-1545">Reconciliation</h1>
        <div className="flex flex-wrap gap-2" data-id="element-1546">
          <button className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" data-id="element-1547">
            <FileUpIcon className="h-5 w-5 mr-1" data-id="element-1548" />
            <span data-id="element-1549">Upload Billing File</span>
          </button>
          <button className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" onClick={() => setIsExportModalOpen(true)} aria-label="Export data to CSV" data-id="element-1550">
            <FileDownIcon className="h-5 w-5 mr-1" data-id="element-1551" />
            <span data-id="element-1552">Export</span>
          </button>
          <button className={`inline-flex items-center px-4 py-2 border ${showFilters ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-300 text-gray-700'} rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`} onClick={() => setShowFilters(!showFilters)} data-id="element-1553">
            <FilterIcon className="h-5 w-5 mr-1" data-id="element-1554" />
            <span data-id="element-1555">Filter</span>
          </button>
        </div>
      </div>
      {showFilters && <div className="bg-white rounded-lg shadow p-4 border border-gray-200 space-y-4" data-id="element-1556">
          <div className="flex justify-between items-center" data-id="element-1557">
            <h2 className="text-lg font-medium text-gray-900" data-id="element-1558">Filters</h2>
            <button className="text-gray-500 hover:text-gray-700" onClick={clearFilters} data-id="element-1559">
              Clear All
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1560">
            <div data-id="element-1561">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1562">
                Status
              </label>
              <select className="block w-full px-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" value={filters.status} onChange={e => handleFilterChange('status', e.target.value)} data-id="element-1563">
                <option value="" data-id="element-1564">All Statuses</option>
                <option value="complete" data-id="element-1565">Complete</option>
                <option value="partial" data-id="element-1566">Partial</option>
                <option value="pending" data-id="element-1567">Pending</option>
              </select>
            </div>
            <div data-id="element-1568">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1569">
                Tour Code
              </label>
              <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="Enter tour code..." value={filters.tourCode} onChange={e => handleFilterChange('tourCode', e.target.value)} data-id="element-1570" />
            </div>
            <div data-id="element-1571">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1572">
                Variance
              </label>
              <select className="block w-full px-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" value={filters.variance} onChange={e => handleFilterChange('variance', e.target.value)} data-id="element-1573">
                <option value="" data-id="element-1574">Any Variance</option>
                <option value="zero" data-id="element-1575">No Variance (0.00)</option>
                <option value="positive" data-id="element-1576">Positive Variance ({'>'} 0.00)</option>
                <option value="negative" data-id="element-1577">Negative Variance ({'<'} 0.00)</option>
              </select>
            </div>
            <div data-id="element-1578">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1579">
                Date Range
              </label>
              <DateRangePicker value={filters.dateRange} onChange={value => handleFilterChange('dateRange', value)} data-id="element-1580" />
            </div>
            <div data-id="element-1581">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1582">
                Airline Code
              </label>
              <MultiSelect options={[{
            value: 'SQ',
            label: 'SQ - Singapore Airlines'
          }, {
            value: 'QR',
            label: 'QR - Qatar Airways'
          }, {
            value: 'EK',
            label: 'EK - Emirates'
          }, {
            value: 'CX',
            label: 'CX - Cathay Pacific'
          }, {
            value: 'MH',
            label: 'MH - Malaysia Airlines'
          }]} value={filters.airlineCodes} onChange={value => handleFilterChange('airlineCodes', value)} placeholder="Select airlines..." data-id="element-1583" />
            </div>
          </div>
          <div className="flex justify-end pt-2" data-id="element-1584">
            <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" data-id="element-1585">
              Apply Filters
            </button>
          </div>
        </div>}
      <div className="bg-white rounded-lg shadow overflow-hidden" data-id="element-1586">
        <div className="p-4 border-b border-gray-200" data-id="element-1587">
          <div className="relative" data-id="element-1588">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1589">
              <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-1590" />
            </div>
            <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="Search by group ID or tour code..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-1591" />
          </div>
        </div>
        {filteredData.length > 0 ? <div className="overflow-x-auto" data-id="element-1592">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-1593">
              <thead className="bg-gray-50" data-id="element-1594">
                <tr data-id="element-1595">
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1596">
                    Group ID
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1597">
                    Tour Code
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1598">
                    Tickets
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1599">
                    Matches
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1600">
                    System Total
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1601">
                    Billing Total
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1602">
                    Variance
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1603">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1604">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-1605">
                {filteredData.map((item, index) => <tr key={item.id} className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50`} data-id="element-1606">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1607">
                      <Link to={`/tickets/${item.groupId}`} className="text-blue-600 hover:text-blue-900" data-id="element-1608">
                        {item.groupId}
                      </Link>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1609">
                      {item.tourCode}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1610">
                      {item.ticketCount}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1611">
                      <div className="flex items-center space-x-1" data-id="element-1612">
                        <span className="text-green-600" data-id="element-1613">
                          {item.matchedCount}
                        </span>
                        <span data-id="element-1614">/</span>
                        <span className="text-red-600" data-id="element-1615">
                          {item.mismatchCount}
                        </span>
                        <span data-id="element-1616">/</span>
                        <span className="text-gray-600" data-id="element-1617">
                          {item.unmatchedCount}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1618">
                      ${item.systemTotal.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1619">
                      ${item.billingTotal.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" data-id="element-1620">
                      <span className={item.variance === 0 ? 'text-green-600' : 'text-red-600'} data-id="element-1621">
                        ${Math.abs(item.variance).toFixed(2)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-1622">
                      <StatusBadge status={item.status === 'complete' ? 'success' : item.status === 'partial' ? 'warning' : 'pending'} label={item.status === 'complete' ? 'Complete' : item.status === 'partial' ? 'Partial' : 'Pending'} data-id="element-1623" />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-1624">
                      <Link to={`/tickets/${item.groupId}`} className="text-blue-600 hover:text-blue-900" data-id="element-1625">
                        View
                      </Link>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div> : <div className="py-8" data-id="element-1626">
            <EmptyState title="No reconciliation data found" description="Upload a billing file to start the reconciliation process" icon={<FileTextIcon className="h-8 w-8 text-gray-400" data-id="element-1628" />} actionLabel="Upload Billing File" actionLink="#" data-id="element-1627" />
          </div>}
      </div>
      <div className="bg-white rounded-lg shadow p-6" data-id="element-1629">
        <h2 className="text-lg font-medium text-gray-900 mb-4" data-id="element-1630">
          Reconciliation Summary
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-1631">
          <div className="bg-green-50 rounded-lg p-4 border border-green-200" data-id="element-1632">
            <div className="flex items-center" data-id="element-1633">
              <CheckCircleIcon className="h-8 w-8 text-green-500 mr-3" data-id="element-1634" />
              <div data-id="element-1635">
                <p className="text-sm text-green-700 mb-1" data-id="element-1636">Complete</p>
                <p className="text-xl font-bold text-green-800" data-id="element-1637">1</p>
                <p className="text-xs text-green-600" data-id="element-1638">
                  Fully reconciled groups
                </p>
              </div>
            </div>
          </div>
          <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200" data-id="element-1639">
            <div className="flex items-center" data-id="element-1640">
              <AlertCircleIcon className="h-8 w-8 text-yellow-500 mr-3" data-id="element-1641" />
              <div data-id="element-1642">
                <p className="text-sm text-yellow-700 mb-1" data-id="element-1643">Partial</p>
                <p className="text-xl font-bold text-yellow-800" data-id="element-1644">1</p>
                <p className="text-xs text-yellow-600" data-id="element-1645">
                  Partially reconciled groups
                </p>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-1646">
            <div className="flex items-center" data-id="element-1647">
              <HelpCircleIcon className="h-8 w-8 text-gray-500 mr-3" data-id="element-1648" />
              <div data-id="element-1649">
                <p className="text-sm text-gray-700 mb-1" data-id="element-1650">Pending</p>
                <p className="text-xl font-bold text-gray-800" data-id="element-1651">1</p>
                <p className="text-xs text-gray-600" data-id="element-1652">Unreconciled groups</p>
              </div>
            </div>
          </div>
          <div className="bg-red-50 rounded-lg p-4 border border-red-200" data-id="element-1653">
            <div className="flex items-center" data-id="element-1654">
              <AlertCircleIcon className="h-8 w-8 text-red-500 mr-3" data-id="element-1655" />
              <div data-id="element-1656">
                <p className="text-sm text-red-700 mb-1" data-id="element-1657">Total Variance</p>
                <p className="text-xl font-bold text-red-800" data-id="element-1658">$9,000.00</p>
                <p className="text-xs text-red-600" data-id="element-1659">Across all groups</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      {isExportModalOpen && <ExportModal onClose={() => setIsExportModalOpen(false)} data-id="element-1660" />}
    </div>;
};
export default Reconciliation;